import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { AUTH_CONFIG, AuthConfig, DEFAULT_AUTH_CONFIG } from '../auth.config';
import { User, TokenPayload, SessionData } from '../models/auth.models';

/**
 * Token Service
 * Handles all token storage and retrieval operations
 *
 * Storage Strategy:
 * - Access Token: BehaviorSubject (in-memory only - most secure)
 * - Refresh Token: Cookie (HTTP-only for security)
 * - User Data: BehaviorSubject (in-memory)
 * - Session Info: BehaviorSubject (in-memory)
 *
 * Benefits:
 * - XSS attacks cannot steal tokens from memory
 * - Tokens are automatically cleared on page close
 * - Refresh token in cookie allows silent re-authentication
 */
@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private config: AuthConfig;
  private isBrowser: boolean;

  // ==================== BehaviorSubjects for In-Memory Storage ====================

  // Auth Tokens
  private accessToken$ = new BehaviorSubject<string | null>(null);
  private refreshToken$ = new BehaviorSubject<string | null>(null);

  // User Data
  private user$ = new BehaviorSubject<User | null>(null);
  private sessionId$ = new BehaviorSubject<string | null>(null);
  private userId$ = new BehaviorSubject<string | null>(null);
  private accountId$ = new BehaviorSubject<string | null>(null);
  private roleId$ = new BehaviorSubject<string | null>(null);
  private username$ = new BehaviorSubject<string | null>(null);

  // Profile Info
  private firstName$ = new BehaviorSubject<string | null>(null);
  private lastName$ = new BehaviorSubject<string | null>(null);
  private fullName$ = new BehaviorSubject<string | null>(null);
  private profilePath$ = new BehaviorSubject<string | null>(null);

  // Account/Branding
  private accountDisplayName$ = new BehaviorSubject<string | null>(null);
  private logo$ = new BehaviorSubject<string | null>(null);
  private productDisplayName$ = new BehaviorSubject<string | null>(null);

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Optional() @Inject(AUTH_CONFIG) config: AuthConfig
  ) {
    this.config = config || DEFAULT_AUTH_CONFIG;
    this.isBrowser = isPlatformBrowser(platformId);

    // Try to restore state from local/session storage on initialization
    this.initializeFromStorage();

    // Try to restore refresh token from cookie on initialization
    this.initializeFromCookie();
  }

  /**
   * Initialize state from storage (sessionStorage for fallback values)
   */
  private initializeFromStorage(): void {
    if (!this.isBrowser) return;

    const accessToken = sessionStorage.getItem('access_token');
    if (accessToken) this.accessToken$.next(accessToken);

    const sessionId = sessionStorage.getItem('session_id');
    if (sessionId) this.sessionId$.next(sessionId);

    const userId = sessionStorage.getItem('user_id');
    if (userId) this.userId$.next(userId);

    const accountId = sessionStorage.getItem('account_id');
    if (accountId) this.accountId$.next(accountId);

    const roleId = sessionStorage.getItem('role_id');
    if (roleId) this.roleId$.next(roleId);

    const accountDisplayName = sessionStorage.getItem('account_display_name');
    if (accountDisplayName) this.accountDisplayName$.next(accountDisplayName);

    const accountLogo = sessionStorage.getItem('account_logo');
    if (accountLogo) this.logo$.next(accountLogo);

    const profilePath = sessionStorage.getItem('profile_path');
    if (profilePath) this.profilePath$.next(profilePath);

    const productDisplayName = sessionStorage.getItem('product_display_name');
    if (productDisplayName) this.productDisplayName$.next(productDisplayName);
  }

  /**
   * Initialize refresh token from cookie if available
   */
  private initializeFromCookie(): void {
    if (this.config.useHttpOnlyCookie) {
      const refreshToken = this.getCookie(this.config.refreshTokenKey);
      if (refreshToken) {
        this.refreshToken$.next(refreshToken);
      }
    }
  }

  // ==================== Cookie Helpers ====================

  /**
   * Set a cookie
   */
  private setCookie(name: string, value: string, days?: number): void {
    if (!this.isBrowser) return;

    const expireDays = days || this.config.cookieExpireDays;
    const date = new Date();
    date.setTime(date.getTime() + expireDays * 24 * 60 * 60 * 1000);

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
    cookieString += `; expires=${date.toUTCString()}`;
    cookieString += `; path=${this.config.cookiePath}`;

    if (this.config.cookieDomain) {
      cookieString += `; domain=${this.config.cookieDomain}`;
    }

    if (this.config.cookieSecure) {
      cookieString += '; secure';
    }

    cookieString += `; samesite=${this.config.cookieSameSite}`;

    document.cookie = cookieString;
  }

  /**
   * Get a cookie value by name
   */
  private getCookie(name: string): string | null {
    if (!this.isBrowser) return null;

    const nameEQ = encodeURIComponent(name) + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  /**
   * Delete a cookie
   */


  private deleteCookie(name: string): void {
    if (!this.isBrowser) return;

    let cookieString = `${encodeURIComponent(name)}=`;
    cookieString += '; expires=Thu, 01 Jan 1970 00:00:00 UTC';
    cookieString += `; path=${this.config.cookiePath}`;

    if (this.config.cookieDomain) {
      cookieString += `; domain=${this.config.cookieDomain}`;
    }

    document.cookie = cookieString;
  }

  // ==================== Access Token (In-Memory BehaviorSubject) ====================

  /**
   * Get the access token (current value)
   */
  getAccessToken(): string | null {
    return this.accessToken$.getValue();
  }

  /**
   * Get access token as Observable
   */
  getAccessToken$(): Observable<string | null> {
    return this.accessToken$.asObservable();
  }

  /**
   * Set the access token
   */
  setAccessToken(token: string): void {
    this.accessToken$.next(token);
    if (this.isBrowser) {
      sessionStorage.setItem('access_token', token);
    }
  }

  /**
   * Remove the access token
   */
  removeAccessToken(): void {
    this.accessToken$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('access_token');
    }
  }

  /**
   * Check if access token exists
   */
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  // ==================== Refresh Token (Cookie + BehaviorSubject) ====================

  /**
   * Get the refresh token (current value)
   */
  getRefreshToken(): string | null {
    // First check BehaviorSubject
    const memoryToken = this.refreshToken$.getValue();
    if (memoryToken) return memoryToken;

    // Fallback to cookie
    if (this.config.useHttpOnlyCookie) {
      return this.getCookie(this.config.refreshTokenKey);
    }
    return null;
  }


  /**
   * Get refresh token as Observable
   */
  getRefreshToken$(): Observable<string | null> {
    return this.refreshToken$.asObservable();
  }

  /**
   * Set the refresh token (stored in cookie + BehaviorSubject)
   */
  setRefreshToken(token: string): void {
    this.refreshToken$.next(token);

    // Also store in cookie for persistence across page reloads
    if (this.config.useHttpOnlyCookie) {
      this.setCookie(this.config.refreshTokenKey, token);
    }
  }

  /**
   * Remove the refresh token
   */
  removeRefreshToken(): void {
    this.refreshToken$.next(null);

    if (this.config.useHttpOnlyCookie) {
      this.deleteCookie(this.config.refreshTokenKey);
    }
  }

  /**
   * Check if refresh token exists
   */
  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  // ==================== Session ID (In-Memory BehaviorSubject) ====================

  /**
   * Get session ID
   */
  getSessionId(): string | null {
    return this.sessionId$.getValue();
  }

  /**
   * Get session ID as Observable
   */
  getSessionId$(): Observable<string | null> {
    return this.sessionId$.asObservable();
  }

  /**
   * Set session ID
   */
  setSessionId(sessionId: string): void {
    this.sessionId$.next(sessionId);
    if (this.isBrowser) {
      sessionStorage.setItem('session_id', sessionId);
    }
  }

  /**
   * Remove session ID
   */
  removeSessionId(): void {
    this.sessionId$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('session_id');
    }
  }

  // ==================== Account ID (In-Memory BehaviorSubject) ====================

  /**
   * Get account ID
   */
  getAccountId(): string | null {
    return this.accountId$.getValue();
  }

  /**
   * Get account ID as Observable
   */
  getAccountId$(): Observable<string | null> {
    return this.accountId$.asObservable();
  }

  /**
   * Set account ID
   */
  setAccountId(accountId: string | number): void {
    const value = String(accountId);
    this.accountId$.next(value);
    if (this.isBrowser) {
      sessionStorage.setItem('account_id', value);
    }
  }

  /**
   * Remove account ID
   */
  removeAccountId(): void {
    this.accountId$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('account_id');
    }
  }

  // ==================== User ID (In-Memory BehaviorSubject) ====================

  /**
   * Get user ID
   */
  getUserId(): string | null {
    return this.userId$.getValue();
  }

  /**
   * Get user ID as Observable
   */
  getUserId$(): Observable<string | null> {
    return this.userId$.asObservable();
  }

  /**
   * Set user ID
   */
  setUserId(userId: string | number): void {
    const value = String(userId);
    this.userId$.next(value);
    if (this.isBrowser) {
      sessionStorage.setItem('user_id', value);
    }
  }

  /**
   * Remove user ID
   */
  removeUserId(): void {
    this.userId$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('user_id');
    }
  }

  // ==================== User (In-Memory BehaviorSubject) ====================

  /**
   * Get stored user
   */
  getUser(): User | null {
    return this.user$.getValue();
  }

  /**
   * Get user as Observable
   */
  getUser$(): Observable<User | null> {
    return this.user$.asObservable();
  }

  /**
   * Set user data and all related fields
   */
  setUser(user: User): void {
    this.user$.next(user);

    // Update all related fields from user object
    if (user.user_id) {
      this.setUserId(user.user_id);
      this.setSessionId(String(user.user_id)); // session_id = user_id
    }
    if (user.account_id) {
      this.setAccountId(user.account_id);
    }
    if (user.role_id) {
      this.setRoleId(user.role_id);
    }
    if (user.username) {
      this.setUsername(user.username);
    }
    if (user.first_name) {
      this.setFirstName(user.first_name);
    }
    if (user.last_name) {
      this.setLastName(user.last_name);
    }
    // Compute full_name
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    this.setFullName(fullName || user.full_name || '');

    if (user.profile_path) {
      this.setProfilePath(user.profile_path);
    }
    if (user.account_display_name) {
      this.setAccountDisplayName(user.account_display_name);
    }
    if (user.account_logo) {
      this.setLogo(user.account_logo);
    }
    if (user.product_display_name) {
      this.setProductDisplayName(user.product_display_name);
    }
  }

  /**
   * Remove user data
   */
  removeUser(): void {
    this.user$.next(null);
  }

  // ==================== Role ID ====================

  getRoleId(): string | null {
    return this.roleId$.getValue();
  }

  getRoleId$(): Observable<string | null> {
    return this.roleId$.asObservable();
  }

  setRoleId(roleId: string | number): void {
    const value = String(roleId);
    this.roleId$.next(value);
    if (this.isBrowser) {
      sessionStorage.setItem('role_id', value);
    }
  }

  removeRoleId(): void {
    this.roleId$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('role_id');
    }
  }

  // ==================== Username ====================

  getUsername(): string | null {
    return this.username$.getValue();
  }

  getUsername$(): Observable<string | null> {
    return this.username$.asObservable();
  }

  setUsername(username: string): void {
    this.username$.next(username);
  }

  removeUsername(): void {
    this.username$.next(null);
  }

  // ==================== First Name ====================

  getFirstName(): string | null {
    return this.firstName$.getValue();
  }

  getFirstName$(): Observable<string | null> {
    return this.firstName$.asObservable();
  }

  setFirstName(firstName: string): void {
    this.firstName$.next(firstName);
  }

  removeFirstName(): void {
    this.firstName$.next(null);
  }

  // ==================== Last Name ====================

  getLastName(): string | null {
    return this.lastName$.getValue();
  }

  getLastName$(): Observable<string | null> {
    return this.lastName$.asObservable();
  }

  setLastName(lastName: string): void {
    this.lastName$.next(lastName);
  }

  removeLastName(): void {
    this.lastName$.next(null);
  }

  // ==================== Full Name ====================

  getFullName(): string | null {
    return this.fullName$.getValue();
  }

  getFullName$(): Observable<string | null> {
    return this.fullName$.asObservable();
  }

  setFullName(fullName: string): void {
    this.fullName$.next(fullName);
  }

  removeFullName(): void {
    this.fullName$.next(null);
  }

  // ==================== Profile Path ====================

  getProfilePath(): string | null {
    return this.profilePath$.getValue();
  }

  getProfilePath$(): Observable<string | null> {
    return this.profilePath$.asObservable();
  }

  setProfilePath(profilePath: string): void {
    this.profilePath$.next(profilePath);
    if (this.isBrowser) {
      sessionStorage.setItem('profile_path', profilePath);
    }
  }

  removeProfilePath(): void {
    this.profilePath$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('profile_path');
    }
  }

  // ==================== Account Display Name ====================

  getAccountDisplayName(): string | null {
    return this.accountDisplayName$.getValue();
  }

  getAccountDisplayName$(): Observable<string | null> {
    return this.accountDisplayName$.asObservable();
  }

  setAccountDisplayName(name: string): void {
    this.accountDisplayName$.next(name);
    if (this.isBrowser) {
      sessionStorage.setItem('account_display_name', name);
    }
  }

  removeAccountDisplayName(): void {
    this.accountDisplayName$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('account_display_name');
    }
  }

  // ==================== Logo ====================

  getLogo(): string | null {
    return this.logo$.getValue();
  }

  getLogo$(): Observable<string | null> {
    return this.logo$.asObservable();
  }

  setLogo(logo: string): void {
    this.logo$.next(logo);
    if (this.isBrowser) {
      sessionStorage.setItem('account_logo', logo);
    }
  }

  removeLogo(): void {
    this.logo$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('account_logo');
    }
  }

  // ==================== Product Display Name ====================

  getProductDisplayName(): string | null {
    return this.productDisplayName$.getValue();
  }

  getProductDisplayName$(): Observable<string | null> {
    return this.productDisplayName$.asObservable();
  }

  setProductDisplayName(name: string): void {
    this.productDisplayName$.next(name);
    if (this.isBrowser) {
      sessionStorage.setItem('product_display_name', name);
    }
  }

  removeProductDisplayName(): void {
    this.productDisplayName$.next(null);
    if (this.isBrowser) {
      sessionStorage.removeItem('product_display_name');
    }
  }

  // ==================== Token Utilities ====================

  /**
   * Decode JWT token
   */
  decodeToken(token: string): TokenPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as TokenPayload;
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  }

  /**
   * Get token expiration time in seconds
   */
  getTokenExpiration(token?: string): number | null {
    const t = token || this.getAccessToken();
    if (!t) return null;

    const decoded = this.decodeToken(t);
    return decoded?.exp || null;
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token?: string): boolean {
    const exp = this.getTokenExpiration(token);
    if (!exp) return false; // If no expiration claim, assume valid (fallback to session check)

    const now = Math.floor(Date.now() / 1000);
    return exp < now;
  }

  /**
   * Get seconds until token expires
   */
  getSecondsUntilExpiry(token?: string): number {
    const exp = this.getTokenExpiration(token);
    if (!exp) return 0;

    const now = Math.floor(Date.now() / 1000);
    return Math.max(0, exp - now);
  }

  /**
   * Check if token should be refreshed
   */
  shouldRefreshToken(): boolean {
    const secondsUntilExpiry = this.getSecondsUntilExpiry();
    return secondsUntilExpiry > 0 && secondsUntilExpiry <= this.config.refreshTokenBeforeExpiry;
  }

  // ==================== Session Management ====================

  /**
   * Save complete session data (all in BehaviorSubjects)
   */
  saveSession(data: SessionData): void {
    if (data.access_token) {
      this.setAccessToken(data.access_token);
    }
    if (data.refresh_token) {
      this.setRefreshToken(data.refresh_token);
    }
    if (data.user) {
      this.setUser(data.user);
    }

    // Generate session ID
    const sessionId = this.generateSessionId();
    this.setSessionId(sessionId);
  }

  /**
   * Get complete session data
   */
  getSession(): SessionData | null {
    const accessToken = this.getAccessToken();
    const user = this.getUser();

    if (!accessToken || !user) return null;

    return {
      access_token: accessToken,
      refresh_token: this.getRefreshToken() || undefined,
      user
    };
  }

  /**
   * Clear all session data (clears all BehaviorSubjects)
   */
  clearSession(): void {
    // Tokens
    this.removeAccessToken();
    this.removeRefreshToken();

    // User data
    this.removeUser();
    this.removeSessionId();
    this.removeUserId();
    this.removeAccountId();
    this.removeRoleId();
    this.removeUsername();

    // Profile info
    this.removeFirstName();
    this.removeLastName();
    this.removeFullName();
    this.removeProfilePath();

    // Account/Branding
    this.removeAccountDisplayName();
    this.removeLogo();
    this.removeProductDisplayName();
  }

  /**
   * Check if user is authenticated (has valid token in memory)
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;

    // If token has expiration, check if valid
    const exp = this.getTokenExpiration(token);
    if (exp) {
      return !this.isTokenExpired(token);
    }

    // If no expiration info, check for session_id
    return !!this.getSessionId();
  }

  /**
   * Check if can restore session (has refresh token in cookie)
   */
  canRestoreSession(): boolean {
    return this.hasRefreshToken();
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomPart}`;
  }

  // ==================== Observable Selectors ====================

  /**
   * Get all auth-related observables for components to subscribe
   */
  get tokens$() {
    return {
      // Tokens
      accessToken$: this.accessToken$.asObservable(),
      refreshToken$: this.refreshToken$.asObservable(),

      // User data
      user$: this.user$.asObservable(),
      sessionId$: this.sessionId$.asObservable(),
      userId$: this.userId$.asObservable(),
      accountId$: this.accountId$.asObservable(),
      roleId$: this.roleId$.asObservable(),
      username$: this.username$.asObservable(),

      // Profile info
      firstName$: this.firstName$.asObservable(),
      lastName$: this.lastName$.asObservable(),
      fullName$: this.fullName$.asObservable(),
      profilePath$: this.profilePath$.asObservable(),

      // Account/Branding
      accountDisplayName$: this.accountDisplayName$.asObservable(),
      logo$: this.logo$.asObservable(),
      productDisplayName$: this.productDisplayName$.asObservable()
    };
  }

  /**
   * Get all current values as an object (for debugging or one-time use)
   */
  getAllValues() {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
      user: this.getUser(),
      sessionId: this.getSessionId(),
      userId: this.getUserId(),
      accountId: this.getAccountId(),
      roleId: this.getRoleId(),
      username: this.getUsername(),
      firstName: this.getFirstName(),
      lastName: this.getLastName(),
      fullName: this.getFullName(),
      profilePath: this.getProfilePath(),
      accountDisplayName: this.getAccountDisplayName(),
      logo: this.getLogo(),
      productDisplayName: this.getProductDisplayName()
    };
  }
}
